import RecoveryHelp from "../securityRecovery/RecoveryHelp";
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import {
  operationsLogin,
  operationsLogout,
  getOperationsUser,
  startOperations2FAEnrollment,
  confirmOperations2FAEnrollment,
  verifyOperations2FA,
} from "../operations/operationsApi";
import { getAdminOverview } from "./adminApi";
import "./AdminDashboard.css";
import "./OwnerLogin.css";

const ADMIN_ROLES = ["OWNER", "MANAGER"];

export default function AdminLogin() {
  const { restaurantId } = useParams(); // Route parameter contains the restaurant slug.
  const navigate = useNavigate();
  const dashboard = `/admin/${restaurantId}/dashboard`;
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [recoveryCode, setRecoveryCode] = useState("");
  const [method, setMethod] = useState("authenticator");
  const [step, setStep] = useState("password");
  const [configUrl, setConfigUrl] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [codesSaved, setCodesSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    getAdminOverview(restaurantId)
      .then(() => { if (active) navigate(dashboard, { replace: true }); })
      .catch(() => {})
      .finally(() => { if (active) setChecking(false); });
    return () => { active = false; };
  }, [restaurantId, dashboard, navigate]);

  async function run(action) {
    if (busy) return;
    setError("");
    setBusy(true);
    try {
      await action();
    } catch (err) {
      setError(err.message || "Request failed. Please try again.");
      if (err.status === 401 && step !== "password") {
        setStep("password");
        setOtp("");
        setRecoveryCode("");
        setConfigUrl("");
      }
    } finally {
      setBusy(false);
    }
  }

  async function enterAdmin() {
    // The backend must enforce both restaurant role and completed 2FA.
    await getAdminOverview(restaurantId);
    navigate(dashboard, { replace: true });
  }

  function submitPassword(event) {
    event.preventDefault();
    run(async () => {
      const result = await operationsLogin(restaurantId, username.trim(), password);
      setPassword("");
      if (!ADMIN_ROLES.includes(result.role)) {
        if (result.authenticated) await operationsLogout(restaurantId).catch(() => {});
        throw new Error("Only OWNER and MANAGER can access Restaurant Admin.");
      }
      if (result.requires_2fa === true) {
        setStep(result.requires_enrollment ? "enrollment" : "verification");
      } else if (result.authenticated === true) {
        await enterAdmin();
      } else {
        throw new Error("Unexpected login response. Please try again.");
      }
    });
  }

  function startEnrollment() {
    run(async () => {
      const result = await startOperations2FAEnrollment(restaurantId);
      if (!result.config_url) throw new Error("Authenticator setup URL was not returned.");
      setConfigUrl(result.config_url);
      setStep("enrollment-confirm");
    });
  }

  function confirmEnrollment(event) {
    event.preventDefault();
    run(async () => {
      const result = await confirmOperations2FAEnrollment(restaurantId, otp.trim());
      if (!result.authenticated || !Array.isArray(result.recovery_codes)) {
        throw new Error("Authenticator setup did not complete.");
      }
      setOtp("");
      setRecoveryCodes(result.recovery_codes);
      setCodesSaved(false);
      setStep("recovery-codes");
    });
  }

  function submitVerification(event) {
    event.preventDefault();
    run(async () => {
      const payload = method === "authenticator"
        ? { token: otp.trim() }
        : { recovery_code: recoveryCode.trim() };
      const result = await verifyOperations2FA(restaurantId, payload);
      if (!result.authenticated) throw new Error("Two-factor verification did not complete.");
      setOtp("");
      setRecoveryCode("");
      await enterAdmin();
    });
  }

  if (checking) return <main className="ra-login-page"><div className="ra-login-card">Checking your login...</div></main>;

  return (
    <main className="ra-login-page">
      <div className="ra-login-card">
        <div className="ra-login-brand"><div className="ra-login-mark" aria-hidden="true">F</div><div className="ra-login-brand-text"><strong>FoodHorizon</strong><span>Restaurant management portal</span></div></div>
        <h1>Restaurant Admin</h1>
        <p>Owner and Manager secure sign in — {restaurantId}</p>

        {step === "password" && (
          <form onSubmit={submitPassword}>
            <label>Username<input autoComplete="username" required value={username} onChange={e => setUsername(e.target.value)} /></label>
            <label>Password<input type="password" autoComplete="current-password" required value={password} onChange={e => setPassword(e.target.value)} /></label>
            {error && <div className="ra-error" role="alert">{error}</div>}
            <button disabled={busy} type="submit">{busy ? "Signing in…" : "Sign in"}</button>
          </form>
        )}

        {step === "enrollment" && (
          <div>
            <h2>Set up two-factor authentication</h2>
            <p>Use an authenticator app to protect your account.</p>
            {error && <div className="ra-error" role="alert">{error}</div>}
            <button type="button" disabled={busy} onClick={startEnrollment}>{busy ? "Preparing…" : "Set up authenticator"}</button>
          </div>
        )}

        {step === "enrollment-confirm" && (
          <form onSubmit={confirmEnrollment}>
            <h2>Scan your QR code</h2>
            <p>Scan this QR code with your authenticator app and enter its six-digit code.</p>
            {configUrl && <div className="ra-login-qr"><QRCodeSVG value={configUrl} size={192} /></div>}
            <label>Authenticator code<input inputMode="numeric" autoComplete="one-time-code" maxLength={6} pattern="[0-9]{6}" value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))} required /></label>
            {error && <div className="ra-error" role="alert">{error}</div>}
            <button type="submit" disabled={busy || otp.length !== 6}>{busy ? "Verifying…" : "Verify and finish setup"}</button>
          </form>
        )}

        {step === "verification" && (
          <form onSubmit={submitVerification}>
            <h2>Two-factor verification</h2>
            <div className="ra-login-methods">
              <label><input type="radio" name="admin-2fa-method" checked={method === "authenticator"} onChange={() => { setMethod("authenticator"); setError(""); }} /> Authenticator</label>
              <label><input type="radio" name="admin-2fa-method" checked={method === "recovery"} onChange={() => { setMethod("recovery"); setError(""); }} /> Recovery code</label>
            </div>
            {method === "authenticator" ? (
              <label>Authenticator code<input inputMode="numeric" autoComplete="one-time-code" maxLength={6} pattern="[0-9]{6}" value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))} required /></label>
            ) : (
              <label>Unused recovery code<input autoComplete="off" maxLength={8} value={recoveryCode} onChange={e => setRecoveryCode(e.target.value.trim().slice(0, 8))} required /></label>
            )}
            {error && <div className="ra-error" role="alert">{error}</div>}
            <button type="submit" disabled={busy || (method === "authenticator" ? otp.length !== 6 : recoveryCode.length !== 8)}>{busy ? "Verifying…" : "Verify and sign in"}</button>
          </form>
        )}

        {step === "recovery-codes" && (
          <div>
            <h2>Save your recovery codes</h2>
            <p>Each recovery code can be used only once. Store them securely; they will not be shown again.</p>
            <div className="ra-login-codes">
              {recoveryCodes.map((code, i) => <code key={i} style={{ overflowWrap: "anywhere" }}>{code}</code>)}
            </div>
            <button type="button" onClick={() => {
              if (navigator.clipboard?.writeText) navigator.clipboard.writeText(recoveryCodes.join("\n")).catch(() => setError("Copy failed. Save codes manually."));
              else setError("Clipboard unavailable. Save codes manually.");
            }}>Copy recovery codes</button>
            {error && <div className="ra-error" role="alert">{error}</div>}
            <label className="ra-login-check"><input type="checkbox" checked={codesSaved} onChange={e => setCodesSaved(e.target.checked)} /> I have securely saved these codes.</label>
            <button type="button" disabled={!codesSaved || busy} onClick={() => run(enterAdmin)}>Continue to dashboard</button>
          </div>
        )}

        <RecoveryHelp audience="staff" onUseCode={() => { setMethod("recovery"); setError(""); }} />
        <Link to={`/operations/${restaurantId}`}>Operations login</Link>
      </div>
    </main>
  );
}